<template>
  <div>
    <h3>{{ t('content.form_title') }}</h3>
    <a-form
      :model="formModel"
      :rules="formRules"
      autocomplete="off"
      @submit.prevent="handleSubmit"
      layout="vertical"
      class="wrapper"
    >
      <div class="header">
        <h4>{{ t('content.structure_title') }}</h4>
      </div>

      <a-form-item name="name" :label="t('content.name')">
        <a-input v-model:value="formModel.name" :disabled="globalLoading" />
      </a-form-item>

      <a-form-item name="source" :label="t('content.source')">
        <a-select
          v-model:value="formModel.source"
          :options="sourceValues"
          @select="selectSource"
          :loading="sourcesLoading"
          @dropdownVisibleChange="loadSources"
          :disabled="globalLoading"
          :not-found-content="null"
        >
          <template #notFoundContent>
            <a-spin v-if="sourcesLoading" size="small" />
          </template>
        </a-select>
      </a-form-item>

      <a-form-item name="view" :label="t('content.view')">
        <a-select
          v-model:value="formModel.view"
          :options="viewValues"
          :loading="viewsLoading"
          :disabled="globalLoading"
          :not-found-content="null"
        >
          <template #notFoundContent>
            <a-spin v-if="viewsLoading" size="small" />
          </template>
        </a-select>
      </a-form-item>

      <div class="header">
        <h4>{{ t('content.rule') }}</h4>
      </div>

      <a-form-item name="field" :label="t('content.searchable_field')">
        <a-select
          v-model:value="formModel.field"
          :options="searchableValues"
          :loading="searchableLoading"
          :disabled="globalLoading"
          :not-found-content="null"
        >
          <template #notFoundContent>
            <a-spin v-if="searchableLoading" size="small" />
          </template>
        </a-select>
      </a-form-item>

      <a-form-item name="operator" :label="t('content.operation')">
        <a-select
          v-model:value="formModel.operator"
          show-search
          :options="i18nOperators"
          @change="handleOperatorChange"
          :loading="operatorsLoading"
          :disabled="globalLoading"
          :not-found-content="null"
        >
          <template #notFoundContent>
            <a-spin v-if="operatorsLoading" size="small" />
          </template>
        </a-select>
      </a-form-item>

      <a-form-item v-if="!dynamic" name="value" :label="t('content.value')">
        <a-input
          v-model:value="formModel.value"
          :disabled="globalLoading || inputValueDisabled"
        />
        <a
          v-if="
            !inputValueDisabled &&
            !dynamic &&
            (!fromModal || (fromModal && userDb != null))
          "
          class="lng-link"
          @click="useDynamic"
          >{{ t('content.dynamic_field') }}</a
        >
      </a-form-item>

      <a-form-item v-if="dynamic" name="userDb" :label="t('content.content')">
        <a-select
          v-model:value="formModel.userDb"
          show-search
          :filter-option="filterOption"
          option-filter-prop="children"
          :options="userDbValues"
          @dropdownVisibleChange="loadUserDbs"
          @select="selectUserDb"
          :loading="userDbsLoading"
          :disabled="globalLoading || inputValueDisabled"
          :not-found-content="null"
        >
          <template #notFoundContent>
            <a-spin v-if="userDbsLoading" size="small" />
          </template>
        </a-select>
      </a-form-item>

      <a-form-item
        v-if="dynamic"
        name="userDbField"
        :label="t('content.field')"
      >
        <a-select
          v-model:value="formModel.userDbField"
          show-search
          :filter-option="filterOption"
          option-filter-prop="children"
          :options="fieldValues"
          :loading="userDbFieldsLoading"
          :disabled="globalLoading || inputValueDisabled"
          :not-found-content="null"
        >
          <template #notFoundContent>
            <a-spin v-if="userDbFieldsLoading" size="small" />
          </template>
        </a-select>
        <a
          v-if="!inputValueDisabled && dynamic"
          class="link"
          @click="useStatic"
          >{{ t('content.static_field') }}</a
        >
      </a-form-item>

      <a-form-item class="sbm-button">
        <a-button type="primary" html-type="submit" class="left-btn">
          {{ t('content.save') }}
        </a-button>
      </a-form-item>
    </a-form>
  </div>
</template>

<script>
import {reactive, toRefs, onMounted, computed} from 'vue';
import {notification} from 'ant-design-vue';
import {useStore} from 'vuex';
import {useI18n} from 'vue-i18n';
import ContentClient from './contentClient.js';
import SendClient from './sendClient.js';
import MergeContentClient from './mergeContentClient.js';
import MergeContentService from './mergeContentService.js';

const contentClient = ContentClient(process.env.VUE_APP_CONTENT_API);
const sendClient = SendClient(process.env.VUE_APP_SEND_API);
const mergeContentClient = MergeContentClient(process.env.VUE_APP_ED_API);
const mergeContentService = MergeContentService(
  mergeContentClient,
  contentClient,
  sendClient
);

const buildForm = (originalObject, fields) =>
  fields.reduce((result, field) => {
    result[field] = originalObject[field];
    return result;
  }, {});

const buildMergeContent = (values, userDbs, isValueNeeded) => {
  const result = {
    name: values.name,
    'external-source-id': values.source,
    'view-id': values.view,
    rule: {
      type: 'SIMPLE',
      data: {
        operator: values.operator,
        'external-source-field': values.field
      }
    }
  };
  if (isValueNeeded) {
    if (values.userDb !== undefined && values.userDb !== null) {
      const dbName = userDbs.find(u => u.id === values.userDb)?.name;
      if (dbName) {
        const value = {
          type: 'DYNAMIC',
          data: {
            'merge-tag-source': {
              name: dbName,
              type: 'SEND',
              extended: {
                userDbId: values.userDb
              }
            },
            'merge-tag': values.userDbField
          }
        };
        result.rule.data.value = value;
      }
    } else if (values.value !== undefined && values.value !== '') {
      const value = {
        type: 'STATIC',
        data: {
          'static-value': values.value
        }
      };
      result.rule.data.value = value;
    }
  }
  return result;
};

export default {
  name: 'DynamicContentForm',
  props: {
    dynamicContent: Object,
    userDb: Number,
    fromModal: Boolean
  },
  setup(props, {emit}) {
    const store = useStore();
    const {t} = useI18n();

    const state = reactive({
      formModel: {
        name: '',
        source: '',
        view: '',
        field: '',
        operator: '',
        value: '',
        userDb: null,
        userDbField: ''
      },
      formRules: {
        name: [{required: true, message: '', trigger: 'blur'}],
        source: [{required: true, message: '', trigger: 'change'}],
        view: [{required: true, message: '', trigger: 'change'}],
        field: [{required: true, message: '', trigger: 'change'}],
        operator: [{required: true, message: '', trigger: 'change'}],
        value: [],
        userDb: [],
        userDbField: []
      },
      externalSources: [],
      sourcesLoading: false,
      searchableFields: [],
      searchableLoading: false,
      views: [],
      viewsLoading: false,
      userDbs: [],
      userDbsLoading: false,
      userDbFields: [],
      userDbFieldsLoading: false,
      dynamic: false,
      mergeContentId: undefined,
      globalLoading: true,
      inputValueDisabled: false,
      operatorsLoading: false,
      operators: [
        {value: 'EQ', isValueNeeded: true},
        {value: 'NOT_EQ', isValueNeeded: true},
        {value: 'CONTAINS', isValueNeeded: true},
        {value: 'NOT_CONTAINS', isValueNeeded: true},
        {value: 'GT', isValueNeeded: true},
        {value: 'LT', isValueNeeded: true},
        {value: 'EMPTY', isValueNeeded: false},
        {value: 'NOT_EMPTY', isValueNeeded: false}
      ]
    });

    const setRuleMessages = () => {
      state.formRules.name[0].message = t('content.content_name_error');
      state.formRules.source[0].message = t('content.source_error');
      state.formRules.view[0].message = t('content.view_error');
      state.formRules.field[0].message = t('content.field_error');
      state.formRules.operator[0].message = t('content.userdb_field_error');
    };

    onMounted(async () => {
      setRuleMessages();

      if (props.dynamicContent !== undefined) {
        state.dynamic =
          props.dynamicContent.rule.data.value?.type === 'DYNAMIC';
        try {
          const {result, errors} = await mergeContentService.retrieveFormData(
            store.state.accessToken,
            store.state.customer,
            props.dynamicContent
          );

          state.mergeContentId = result.mergeContentId;
          state.externalSources = result.externalSources || [];
          state.views = result.views || [];
          state.searchableFields = result.searchableFields || [];
          state.operator = result.operator;

          if (!result.dynamic) {
            Object.assign(
              state.formModel,
              buildForm(result, [
                'field',
                'view',
                'source',
                'name',
                'value',
                'operator'
              ])
            );
          } else {
            state.userDbs = result.userDbs || [];
            state.userDbFields = result.userDbFields || [];
            Object.assign(
              state.formModel,
              buildForm(result, [
                'field',
                'view',
                'source',
                'name',
                'userDb',
                'userDbField',
                'operator'
              ])
            );
          }

          handleOperatorChange(state.formModel.operator);

          for (const error of errors) {
            showError(t(error));
          }
        } catch (err) {
          showError(t('Generic Error.'), err.message);
        }
      }
      state.globalLoading = false;
    });

    const sourceValues = computed(() =>
      state.externalSources.map(e => ({value: e.uuid, label: e.name}))
    );
    const searchableValues = computed(() =>
      state.searchableFields.map(f => ({value: f, label: f}))
    );
    const viewValues = computed(() =>
      state.views.map(v => ({value: v.id, label: v.name}))
    );
    const userDbValues = computed(() =>
      state.userDbs.map(u => ({value: u.id, label: `${u.id} - ${u.name}`}))
    );
    const fieldValues = computed(() =>
      state.userDbFields.map(f => ({value: f.name, label: f.name}))
    );
    const i18nOperators = computed(() =>
      state.operators.map(o => ({
        value: o.value,
        label: t(`filters.${o.value}`)
      }))
    );

    function showError(message, description) {
      notification.error({message, description});
    }

    function showSuccess(message, description) {
      notification.success({message, description});
    }

    function loadSources(open) {
      if (!open) return;
      state.sourcesLoading = true;
      contentClient
        .retrieveExternalSources(store.state.accessToken, store.state.customer)
        .then(sources => {
          state.externalSources = Array.isArray(sources) ? sources : [];
        })
        .catch(err => {
          showError(t('content.sources_error_title'), err.message);
          state.externalSources = [];
        })
        .finally(() => {
          state.sourcesLoading = false;
        });
    }

    function loadUserDbs(open) {
      if (!open) return;
      state.userDbsLoading = true;
      sendClient
        .retrieveUserDbs(store.state.accessToken, store.state.customer)
        .then(result => {
          if (props.fromModal && props.userDb != null) {
            result = result.filter(u => u.id === props.userDb);
          }
          state.userDbs = result;
          state.userDbsLoading = false;
        })
        .catch(err => {
          showError(t('content.userdb_error_title'), err.message);
          state.userDbsLoading = false;
        });
    }

    function selectUserDb(value) {
      state.userDbFieldsLoading = true;
      sendClient
        .retrieveFields(store.state.accessToken, store.state.customer, value)
        .then(result => {
          state.userDbFields = result;
          state.userDbFieldsLoading = false;
        })
        .catch(err => {
          showError(t('content.userdb_fields_error_title'), err.message);
          state.userDbFieldsLoading = false;
        });
    }

    function selectSource(value) {
      state.searchableLoading = true;
      state.viewsLoading = true;

      contentClient
        .retrieveViews(store.state.accessToken, store.state.customer, value)
        .then(res => {
          state.views = res;
        })
        .catch(error => {
          showError('Cannot retrieve views', error.message);
        })
        .finally(() => {
          state.viewsLoading = false;
        });

      state.searchableFields = state.externalSources.find(
        e => e.uuid === value
      ).searchableFields;
      state.searchableLoading = false;
    }

    function handleOperatorChange(value) {
      const operatorObj = state.operators.find(o => o.value === value);
      state.inputValueDisabled = operatorObj
        ? !operatorObj.isValueNeeded
        : false;
      if (state.dynamic) {
        if (state.inputValueDisabled) {
          state.formRules.userDb = [];
          state.formRules.userDbField = [];
        } else {
          state.formRules.userDb = [
            {
              required: true,
              message: t('content.userdb_field_error'),
              trigger: 'change'
            }
          ];
          state.formRules.userDbField = [
            {
              required: true,
              message: t('content.userdb_field_error'),
              trigger: 'change'
            }
          ];
        }
      } else {
        if (state.inputValueDisabled) {
          state.formRules.value = [];
        } else {
          state.formRules.value = [
            {required: true, message: t('content.value_error'), trigger: 'blur'}
          ];
        }
      }
    }

    function useDynamic() {
      state.dynamic = true;
      state.formModel.value = '';
      handleOperatorChange(state.formModel.operator);
    }

    function useStatic() {
      state.dynamic = false;
      state.formModel.userDb = null;
      state.formModel.userDbField = '';
      handleOperatorChange(state.formModel.operator);
    }

    function filterOption(input, option) {
      return option.label.toLowerCase().includes(input.toLowerCase());
    }

    async function handleSubmit() {
      const isValueNeeded = !state.inputValueDisabled;
      try {
        state.globalLoading = true;
        const mergeContent = buildMergeContent(
          state.formModel,
          state.userDbs,
          isValueNeeded
        );

        if (state.mergeContentId !== undefined) {
          await mergeContentClient.updateMergeContent(
            store.state.accessToken,
            store.state.customer,
            state.mergeContentId,
            mergeContent
          );
          showSuccess(
            t('content.saved_content_title'),
            t('content.saved_content_message', {name: mergeContent.name})
          );
        } else {
          const result = await mergeContentClient.saveMergeContent(
            store.state.accessToken,
            store.state.customer,
            mergeContent
          );
          state.mergeContentId = result.id;
          showSuccess(
            t('content.saved_content_title'),
            t('content.saved_content_message', {name: result.name})
          );
        }
        emit('saved', {savedContent: mergeContent});
      } catch (err) {
        showError(t('content.content_save_error'), err.message);
      } finally {
        state.globalLoading = false;
      }
    }

    return {
      ...toRefs(state),
      t,
      sourceValues,
      searchableValues,
      viewValues,
      userDbValues,
      fieldValues,
      i18nOperators,
      loadSources,
      loadUserDbs,
      selectUserDb,
      selectSource,
      handleOperatorChange,
      useDynamic,
      useStatic,
      filterOption,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.wrapper {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.header {
  grid-column: span 4;
  text-align: left;
}
.sbm-button {
  grid-column: 3;
  grid-row: 5;
}
.left-btn {
  margin-left: 75%;
}
h3 {
  margin-top: 2%;
  text-align: left;
}
</style>
